/* eslint-disable */
import { GoogleMap, Marker } from '@react-google-maps/api';
import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { useGoogleMaps } from '../../../contexts/google-map-context/GoogleMapContent';
import BatteryLoader from '../../battery-loader/BatteryLoader';
import MyButton from '../../button/Button';

interface SelectLatLongProperties {
  latitude: string | number | null;
  longitude: string | number | null;
  handleLatLongChange: (
    latitude: number,
    longitude: number,
    address?: string,
    geoLocation?: any[],
    postalCode?: string | null,
    countryCode?: string | null,
  ) => void;
}

const containerStyle = { width: '800px', height: '450px' }; // Map container style

/**
 * Generates an SVG station marker icon encoded as a data URI.
 * @param {string} color - The color code for the marker.
 * @returns {string} The data URI of the generated SVG icon.
 */
const getStationMarkerIcon = (color: string): string => {
  const svg = `
    <svg width="44" height="45" viewBox="0 0 44 45"
      fill="none" xmlns="http://www.w3.org/2000/svg">

      <path
        d="M22 2.91602C14.4186 2.91602 8.25 8.81563 8.25 16.0645C8.25 19.5166 9.82352 24.1073 12.9267 29.7096C15.4189 34.2076 18.3021 38.275 19.8017 40.2988C20.0551 40.6446 20.3864 40.9259 20.7688 41.1197C21.1512 41.3136 21.5739 41.4146 22.0026 41.4146C22.4313 41.4146 22.854 41.3136 23.2363 41.1197C23.6187 40.9259 23.95 40.6446 24.2034 40.2988C25.7005 38.275 28.5863 34.2076 31.0784 29.7096C34.1765 24.1091 35.75 19.5183 35.75 16.0645C35.75 8.81563 29.5814 2.91602 22 2.91602Z"
        fill="${color}"
      />

      <path
        fill-rule="evenodd"
        clip-rule="evenodd"
        d="M23.2551 19.1954H28.9444C29.3221 19.1944 29.6668 19.386 29.8276 19.6883C29.9884 19.9907 29.9367 20.3475 29.6944 20.6041L19.3279 31.6541C19.1341 31.8446 18.8213 31.9 18.5603 31.7892C18.2993 31.6784 18.1517 31.4285 18.2002 31.1757L20.3058 21.7309H15.0504C14.7553 21.7289 14.4766 21.6094 14.2927 21.4052C14.1088 21.202 14.0394 20.9356 14.1033 20.6809L16.9226 9.63769C17.0184 9.24687 17.4083 8.96785 17.8609 8.96591H25.2198C25.5259 8.96202 25.8145 9.08938 25.9951 9.30715C26.1823 9.51812 26.2428 9.79616 26.1591 10.0538L23.2551 19.1954Z"
        fill="white"
      />
    </svg>
  `;

  return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

/**
 * Select location entire popup with buttons
 * @param {SelectLatLongProperties} root0 - The component properties.
 * @param {string | number | null} root0.latitude - The latitude prop.
 * @param {string | number | null} root0.longitude - The longitude prop.
 * @param {Function} root0.handleLatLongChange - The handleLatLongChange prop.
 * @returns {JSX.Element} The rendered component.
 */
function SelectLatLong({ latitude, longitude, handleLatLongChange }: SelectLatLongProperties) {
  const { t } = useTranslation(['sharedComp', 'sharedComponent']);
  const { isLoaded } = useGoogleMaps();

  const [selectedLocation, setSelectedLocation] = useState<{
    lat: number;
    lng: number;
    geoLocation: any[];
    postalCode: string | null;
    countryCode: string | null;
  }>({
    lat: Number(latitude),
    lng: Number(longitude),
    geoLocation: [],
    postalCode: null,
    countryCode: null,
  });
  const [selectedAddress, setSelectedAddress] = useState<string>('');

  // Default center for the map
  const [mapCenter, setMapCenter] = useState({
    lat: 41.8781, // Example: San Francisco
    lng: -87.6298,
  });

  const [markerIcon, setMarkerIcon] = useState<string>(() => {
    if (typeof window !== 'undefined') {
      const color =
        getComputedStyle(document.documentElement).getPropertyValue('--primary-color').trim() ||
        '#00A79D';
      return getStationMarkerIcon(color);
    }
    return getStationMarkerIcon('#00A79D');
  });

  useEffect(() => {
    const color =
      getComputedStyle(document.documentElement).getPropertyValue('--primary-color').trim() ||
      '#00A79D';
    setMarkerIcon(getStationMarkerIcon(color));
  }, []);

  /**
   * Get the Geo Location details
   */
  const getGeoLocation = (place: any) => {
    // Country-specific mapping
    const levelMapping: Record<string, Record<string, { name: string; key: string }>> = {
      // India
      IN: {
        locality: { name: 'City', key: 'city' },
        administrative_area_level_3: { name: 'Taluk', key: 'taluk' },
        administrative_area_level_2: { name: 'District', key: 'district' },
        administrative_area_level_1: { name: 'State', key: 'state' },
        country: { name: 'Country', key: 'country' },
      },

      // Saudi Arabia
      SA: {
        locality: { name: 'City', key: 'city' },
        administrative_area_level_2: { name: 'Governorate', key: 'governorate' },
        administrative_area_level_1: { name: 'Region', key: 'region' },
        country: { name: 'Country', key: 'country' },
      },

      // United Arab Emirates
      AE: {
        locality: { name: 'City', key: 'city' },
        administrative_area_level_1: { name: 'Emirate', key: 'emirate' },
        country: { name: 'Country', key: 'country' },
      },

      // Bahrain
      BH: {
        locality: { name: 'City', key: 'city' },
        administrative_area_level_2: { name: 'District', key: 'district' },
        administrative_area_level_1: { name: 'Governorate', key: 'governorate' },
        country: { name: 'Country', key: 'country' },
      },

      // Kuwait
      KW: {
        locality: { name: 'City', key: 'city' },
        administrative_area_level_2: { name: 'District', key: 'district' },
        administrative_area_level_1: { name: 'Governorate', key: 'governorate' },
        country: { name: 'Country', key: 'country' },
      },

      // Ethiopia
      ET: {
        locality: { name: 'City', key: 'city' },
        administrative_area_level_2: { name: 'Zone', key: 'zone' },
        administrative_area_level_1: { name: 'Region', key: 'region' },
        country: { name: 'Country', key: 'country' },
      },

      // China
      CN: {
        locality: { name: 'City', key: 'city' },
        administrative_area_level_2: { name: 'Prefecture', key: 'prefecture' },
        administrative_area_level_1: { name: 'Province', key: 'province' },
        country: { name: 'Country', key: 'country' },
      },
    };

    // Get country code first
    let countryCode = '';

    place.address_components?.forEach(({ short_name, types }: any) => {
      if (types.includes('country')) {
        countryCode = short_name;
      }
    });

    const mapping = levelMapping[countryCode] || {
      locality: { name: 'City', key: 'city' },
      administrative_area_level_4: {
        name: 'Administrative Area Level 4',
        key: 'level4',
      },
      administrative_area_level_3: {
        name: 'Administrative Area Level 3',
        key: 'level3',
      },
      administrative_area_level_2: {
        name: 'Administrative Area Level 2',
        key: 'level2',
      },
      administrative_area_level_1: {
        name: 'Administrative Area Level 1',
        key: 'level1',
      },
      country: { name: 'Country', key: 'country' },
    };

    const result: Array<{
      name: string;
      key: string;
      value: string;
    }> = [];

    place.address_components?.forEach(({ long_name, types }: any) => {
      const type = types.find((t: any) => mapping[t as keyof typeof mapping]);

      if (type) {
        const { name, key } = mapping[type];

        result.push({
          name,
          key,
          value: long_name,
        });
      }
    });
    return result;
  };

  // Handle map click event to update the selected location
  const onMapClick = useCallback((event: google.maps.MapMouseEvent) => {
    if (event.latLng) {
      const lat = parseFloat(event.latLng.lat().toFixed(5));
      const lng = parseFloat(event.latLng.lng().toFixed(5));
      const geocoder = new google.maps.Geocoder();

      geocoder.geocode({ location: { lat, lng } }, (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const addressComponents = results[0].address_components ?? [];
          const country = addressComponents.find((component) =>
            component.types.includes('country'),
          );
          const countryCode = country?.short_name ?? null; // IN
          const postalCode =
            addressComponents.find((component) => component.types.includes('postal_code'))
              ?.long_name ?? null;
          setSelectedLocation({ lat, lng, geoLocation: getGeoLocation(results[0]), postalCode, countryCode });
          setSelectedAddress(results[0].formatted_address);
        } else {
          setSelectedAddress('');
        }
      });
    }
  }, []);

  useEffect(() => {
    if (latitude && longitude) {
      setMapCenter({
        lat: Number(latitude),
        lng: Number(longitude),
      });
    }
  }, []);

  if (!isLoaded)
    return (
      <div>
        <BatteryLoader />
      </div>
    );

  return (
    <div className="select-latLong-wrap">
      <div className="select-latLong-container">
        <GoogleMap
          mapContainerStyle={containerStyle}
          center={mapCenter}
          zoom={17}
          onClick={onMapClick}>
          <Marker
            icon={{
              url: markerIcon,
              scaledSize: new window.google.maps.Size(44, 44), // Width, Height
            }}
            position={selectedLocation}
          />
        </GoogleMap>
      </div>
      <div className="select-latLong-buttons">
        <MyButton
          label={t('submit', 'Submit')}
          buttonType="submit"
          onClickFunc={() =>
            handleLatLongChange(
              Number(selectedLocation.lat),
              Number(selectedLocation.lng),
              selectedAddress,
              selectedLocation.geoLocation,
              selectedLocation.postalCode,
              selectedLocation.countryCode,
            )
          }
        />
      </div>
    </div>
  );
}

export default SelectLatLong;
